import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import apiEndpoints from "../../../services/api-endpoints";
import { useAppContext } from "../../../state/app/context";
import { useGameContext } from "../../../state/game/context";
import Alert from "../../../util/alert";
import { AppRoutes } from "../../../routes/app-routes";
import stretchLiveApiEndpoints from "../../../services/stretchlive-api-endpoints";

const ApiErrorBoundary = ({ children }) => {
	const { appError } = useAppContext();
	const { gameError } = useGameContext();
	const [_error, setError] = useState(null);
	const promiseRejectionHandler = useCallback((event) => {
		setError(event);
		appError();
		gameError();
	}, []);

	useEffect(() => {
		setError(null);
		window.addEventListener("unhandledrejection", promiseRejectionHandler);
		window.addEventListener("error", promiseRejectionHandler);

		return () => {
			window.removeEventListener("unhandledrejection", promiseRejectionHandler);
			window.removeEventListener("error", promiseRejectionHandler);
		};
	}, [_error]);

	const ignoredErrorEndpoints = [
		apiEndpoints.EVENT_DATA,
		apiEndpoints.SAVE_BOXSCORE,
		stretchLiveApiEndpoints.EVENT_BY_MEDIA
	];

	const isExceptionRequest = (url = "", endpoint) =>
		url.match(new RegExp(`^${endpoint(".+.", ".+.")}{1}$`, "gi"))?.length === 1;

	const processError = (error) => {
		const output = {
			error,
			errorMessage: undefined,
			redirectTo: undefined
		};

		if (error?.reason) {
			const { config, response, message, code } = error.reason;
			const { url } = config || {};

			if (ignoredErrorEndpoints.some((e) => isExceptionRequest(url, e))) {
				// eslint-disable-next-line no-console
				console.warn("Ignoring request error", error);
			} else if (code === "ERR_NETWORK") {
				output.redirectTo = `${
					AppRoutes.NETWORK_ERROR.url
				}?msg=${"API is down"}&err=${error.reason}&url=${url}`;
			} else {
				const { data } = response || {};
				const msg = message || "Unexpected API error";
				const dataMessage = data?.message || "Unexpected API error";
				const dataErrors = data?.errors;
				//
				const allErrors = [msg, dataMessage, dataErrors].filter((m) => m);

				output.errorMessage = allErrors.join(" - ");
			}
		}

		return output;
	};

	const { errorMessage, redirectTo } = processError(_error);
	if (errorMessage) {
		Alert.error(errorMessage);
	}

	return redirectTo ? <Navigate to={redirectTo} /> : children;
};

export default ApiErrorBoundary;
